import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    CircularProgress,
    Alert,
    TextField,
    Chip,
    Tabs,
    Tab,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Axios from "axios";
import { useSnackbar } from "notistack";
import { getAuthHeader } from "../../utils/authToken";
import ConfirmDialog from "../../components/ConfirmDialog";
import { fmtNyDateTime } from "./prepDates";
import { emailPreviewHtml, emailPreviewDoc } from "./reportEmailPreview";

// Review-before-send batch, shown in the same rich layout as the manual "Generate progress
// reports" modal: per-student tabs, editable subject + EN/KO summaries, and side-by-side email
// + PDF preview. The difference from that modal is the SOURCE — these drafts were parked by a
// scheduled REVIEW-mode run and live in the DB, so edits persist through the item PUT and Send
// goes through the batch send route (which re-resolves recipients from the live schedule).

const ITEM_COLOR = { PENDING: "primary", SENT: "success", FAILED: "error", EXCLUDED: "default", SENDING: "warning" };
const EDITABLE_BATCH = ["PENDING_REVIEW", "PARTIALLY_SENT"];

const ReviewBatchModal = ({ open, onClose, programUuid, batchId, onChanged }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}`;
    const auth = { headers: { Authorization: getAuthHeader() } };
    const { enqueueSnackbar } = useSnackbar();
    const confirmRef = useRef();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [batchStatus, setBatchStatus] = useState(null);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [scheduledFor, setScheduledFor] = useState(null);

    // One entry per student (= one batch item), each holding both audience copies + the editable
    // fields. Excluded/sent state lives on item_status.
    const [items, setItems] = useState([]); // ordered
    const [edits, setEdits] = useState({}); // item_id -> { subject, narrative_student, narrative_parent, dirty }
    const [activeItem, setActiveItem] = useState(null); // item_id of the open tab
    const [activeAudience, setActiveAudience] = useState("student"); // which card the email pane shows
    const [preview, setPreview] = useState("email"); // right pane: email | pdf
    const [pdfUrls, setPdfUrls] = useState({}); // item_id -> blob URL
    const [pdfLoading, setPdfLoading] = useState(null); // item_id currently rendering
    const [step, setStep] = useState("review"); // review | sent
    const [sendResult, setSendResult] = useState(null);

    const dropPdfs = () => setPdfUrls((prev) => {
        Object.values(prev).forEach((u) => window.URL.revokeObjectURL(u));
        return {};
    });
    useEffect(() => () => dropPdfs(), []);

    // Fold the flat per-audience drafts into one row per student (item).
    const foldDrafts = (drafts) => {
        const byItem = new Map();
        for (const d of drafts) {
            let row = byItem.get(d.item_id);
            if (!row) {
                row = {
                    item_id: d.item_id, student_id: d.student_id, name: d.name,
                    item_status: d.item_status, subject: d.subject,
                    audiences: {}, warnings: 0,
                };
                byItem.set(d.item_id, row);
            }
            row.audiences[d.audience] = d;
            if (d.warning) row.warnings += 1;
        }
        return [...byItem.values()];
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await Axios.get(`${base}/report-batches/${batchId}/drafts`, auth);
            setBatchStatus(res.data.status);
            setGeneratedAt(res.data.generated_at);
            setScheduledFor(res.data.scheduled_for);
            const folded = foldDrafts(res.data.drafts || []);
            setItems(folded);
            const nextEdits = {};
            folded.forEach((r) => {
                nextEdits[r.item_id] = {
                    subject: r.subject || "",
                    narrative_student: r.audiences.student?.narrative || "",
                    narrative_parent: r.audiences.parent?.narrative || "",
                    dirty: false,
                };
            });
            setEdits(nextEdits);
            setActiveItem(folded.length ? folded[0].item_id : null);
            setActiveAudience(folded[0]?.audiences.student ? "student" : "parent");
            setStep("review");
            setSendResult(null);
        } catch (err) {
            console.error("Load batch drafts error:", err);
            setError(err.response?.data?.error || "Failed to load the batch drafts");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base, batchId]);

    useEffect(() => {
        if (!open || !batchId) return;
        setPreview("email"); dropPdfs();
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, batchId, load]);

    const editable = EDITABLE_BATCH.includes(batchStatus);
    const activeRow = items.find((r) => r.item_id === activeItem) || null;
    const activeEdit = activeItem ? edits[activeItem] : null;
    const audienceKeys = activeRow ? Object.keys(activeRow.audiences) : [];
    const activeAud = activeRow?.audiences[activeAudience] || activeRow?.audiences[audienceKeys[0]] || null;
    const activeNarrative = activeAud
        ? (activeAud.audience === "parent" ? activeEdit?.narrative_parent : activeEdit?.narrative_student)
        : "";

    const setEdit = (itemId, patch) =>
        setEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch, dirty: true } }));

    const pendingCount = items.filter((r) => r.item_status === "PENDING").length;
    const dirtyIds = Object.keys(edits).filter((id) => edits[id]?.dirty);

    // Render the active student's attachment lazily (one puppeteer run server-side per item) and
    // cache the blob url — the PDF can't change while the batch snapshot is fixed.
    const loadPdf = useCallback(async (itemId) => {
        if (!itemId || pdfUrls[itemId]) return;
        setPdfLoading(itemId);
        try {
            const res = await Axios.get(`${base}/report-batches/${batchId}/items/${itemId}/pdf`,
                { ...auth, responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            setPdfUrls((prev) => {
                if (prev[itemId]) { window.URL.revokeObjectURL(url); return prev; }
                return { ...prev, [itemId]: url };
            });
        } catch (err) {
            console.error("Batch PDF error:", err);
            setError("Failed to render the report PDF");
        } finally {
            setPdfLoading(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base, batchId, pdfUrls]);

    useEffect(() => {
        if (step !== "review" || !activeItem || preview !== "pdf") return;
        loadPdf(activeItem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, activeItem, preview]);

    const saveItem = async (itemId) => {
        const e = edits[itemId];
        if (!e) return true;
        await Axios.put(`${base}/report-batches/${batchId}/items/${itemId}`, {
            subject: e.subject,
            narrative_student: e.narrative_student,
            narrative_parent: e.narrative_parent,
        }, auth);
        setEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], dirty: false } }));
        return true;
    };

    const saveAll = async () => {
        setBusy(true);
        try {
            for (const id of dirtyIds) await saveItem(id);
            enqueueSnackbar("Drafts saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.error || "Failed to save", { variant: "error" });
        } finally {
            setBusy(false);
        }
    };

    const toggleInclude = async (row) => {
        setBusy(true);
        try {
            const next = row.item_status === "EXCLUDED" ? "PENDING" : "EXCLUDED";
            await Axios.put(`${base}/report-batches/${batchId}/items/${row.item_id}`, { status: next }, auth);
            setItems((prev) => prev.map((r) => (r.item_id === row.item_id ? { ...r, item_status: next } : r)));
        } catch (err) {
            enqueueSnackbar(err.response?.data?.error || "Failed to update the draft", { variant: "error" });
        } finally {
            setBusy(false);
        }
    };

    const retryItem = async (row) => {
        setBusy(true);
        try {
            await Axios.put(`${base}/report-batches/${batchId}/items/${row.item_id}`, { status: "PENDING" }, auth);
            setItems((prev) => prev.map((r) => (r.item_id === row.item_id ? { ...r, item_status: "PENDING" } : r)));
        } catch (err) {
            enqueueSnackbar(err.response?.data?.error || "Failed to reset the draft", { variant: "error" });
        } finally {
            setBusy(false);
        }
    };

    const doSend = async () => {
        setBusy(true);
        setError("");
        try {
            // Persist any unsaved edits first — the batch send reads the stored item, not the client.
            for (const id of dirtyIds) await saveItem(id);
            const res = await Axios.post(`${base}/report-batches/${batchId}/send`, {}, auth);
            setSendResult(res.data);
            setStep("sent");
            if (onChanged) onChanged();
        } catch (err) {
            console.error("Batch send error:", err);
            setError(err.response?.data?.error || "Failed to send the batch");
        } finally {
            setBusy(false);
        }
    };

    const confirmSend = () => {
        const recipientCount = items
            .filter((r) => r.item_status === "PENDING")
            .reduce((n, r) => n + Object.values(r.audiences).reduce((m, a) => m + (a.to?.length || 0), 0), 0);
        confirmRef.current.open({
            title: `Send ${pendingCount} report${pendingCount === 1 ? "" : "s"}?`,
            message: `${pendingCount} student${pendingCount === 1 ? "" : "s"} → ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}, each with the report attached. Excluded students are skipped. This cannot be undone.`,
            confirmLabel: `Send ${pendingCount}`,
            onConfirm: doSend,
        });
    };

    const confirmDiscard = () => {
        confirmRef.current.open({
            title: "Discard this batch?",
            message: "The drafts are kept for reference but can no longer be sent. The next scheduled run creates a fresh batch.",
            confirmLabel: "Discard",
            onConfirm: async () => {
                setBusy(true);
                try {
                    await Axios.post(`${base}/report-batches/${batchId}/discard`, {}, auth);
                    if (onChanged) onChanged();
                    onClose();
                } catch (err) {
                    enqueueSnackbar(err.response?.data?.error || "Failed to discard", { variant: "error" });
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const audienceChip = (aud) => (aud === "parent" ? "Parent · 한국어" : "Student · EN");

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                Review &amp; send reports
                {scheduledFor && <Chip size="small" label={scheduledFor} />}
                {batchStatus && (
                    <Chip size="small" color={batchStatus === "PENDING_REVIEW" ? "warning" : (batchStatus === "SENT" ? "success" : "default")}
                        label={batchStatus.replace("_", " ").toLowerCase()} />
                )}
                {generatedAt && (
                    <Typography variant="caption" color="text.secondary">generated {fmtNyDateTime(generatedAt)}</Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
                )}

                {!loading && step === "review" && (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                            Review each email before it goes out. The subject and summary are editable; the tables below them
                            come from the data. Recipients come from the report schedule — you edit what the email says, not who gets it.
                        </Typography>

                        {!editable && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                This batch is {String(batchStatus || "").replace("_", " ").toLowerCase()} — drafts are read-only.
                            </Alert>
                        )}
                        {items.length === 0 && (
                            <Alert severity="info">No drafts in this batch — the schedule mails nobody for these students.</Alert>
                        )}

                        {items.length > 0 && (
                            <>
                                <Tabs
                                    value={activeItem}
                                    onChange={(e, v) => { setActiveItem(v); const r = items.find((x) => x.item_id === v); setActiveAudience(r?.audiences.student ? "student" : "parent"); }}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ borderBottom: "1px solid", borderColor: "divider", minHeight: 44, mb: 2 }}
                                >
                                    {items.map((r) => (
                                        <Tab
                                            key={r.item_id}
                                            value={r.item_id}
                                            sx={{ minHeight: 44, textTransform: "none", opacity: r.item_status === "EXCLUDED" ? 0.5 : 1 }}
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <span>{r.name}</span>
                                                    <Chip size="small" variant="outlined"
                                                        label={r.item_status.toLowerCase()}
                                                        color={ITEM_COLOR[r.item_status] || "default"}
                                                        sx={{ height: 18, ".MuiChip-label": { px: 0.75, fontSize: 11 } }} />
                                                    {r.warnings > 0 && <WarningAmberIcon sx={{ fontSize: 15 }} color="warning" />}
                                                    {edits[r.item_id]?.dirty && <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }} />}
                                                </Box>
                                            }
                                        />
                                    ))}
                                </Tabs>

                                {activeRow && (
                                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
                                        {/* Left: editable subject + audience summary cards */}
                                        <Box sx={{ flex: "1 1 460px", minWidth: 380, display: "flex", flexDirection: "column", gap: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Checkbox
                                                    size="small" sx={{ p: 0.5 }}
                                                    checked={activeRow.item_status !== "EXCLUDED"}
                                                    disabled={busy || !editable || activeRow.item_status === "SENT"}
                                                    onChange={() => toggleInclude(activeRow)}
                                                />
                                                <Typography variant="body2" color="text.secondary">
                                                    {activeRow.item_status === "EXCLUDED" ? "Excluded from this send" : "Included in this send"}
                                                </Typography>
                                                {activeRow.item_status === "FAILED" && (
                                                    <Button size="small" onClick={() => retryItem(activeRow)} disabled={busy}>Retry</Button>
                                                )}
                                            </Box>

                                            <TextField
                                                fullWidth size="small" label="Subject"
                                                value={activeEdit?.subject ?? ""}
                                                onChange={(e) => setEdit(activeRow.item_id, { subject: e.target.value })}
                                                disabled={busy || !editable || activeRow.item_status === "SENT"}
                                            />

                                            {audienceKeys.map((audKey) => {
                                                const aud = activeRow.audiences[audKey];
                                                const val = audKey === "parent" ? activeEdit?.narrative_parent : activeEdit?.narrative_student;
                                                const selected = activeAudience === audKey;
                                                return (
                                                    <Box
                                                        key={audKey}
                                                        onClick={() => setActiveAudience(audKey)}
                                                        sx={{
                                                            border: "1px solid",
                                                            borderColor: selected ? "primary.main" : "divider",
                                                            borderRadius: 1, p: 1.5, cursor: "pointer",
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                            <Chip size="small" color={audKey === "parent" ? "secondary" : "default"} label={audienceChip(audKey)} />
                                                            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, textAlign: "right" }}>
                                                                {(aud.to || []).join(", ")}
                                                            </Typography>
                                                        </Box>
                                                        {aud.warning && (
                                                            <Typography variant="caption" color="warning.main" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                                                                <WarningAmberIcon sx={{ fontSize: 14 }} /> {aud.warning}
                                                            </Typography>
                                                        )}
                                                        <TextField
                                                            fullWidth multiline minRows={5} size="small"
                                                            label={audKey === "parent" ? "Summary (Korean)" : "Summary (English)"}
                                                            helperText="The written summary at the top of the email. The tables below it come from the data."
                                                            value={val ?? ""}
                                                            onChange={(e) => setEdit(activeRow.item_id, audKey === "parent" ? { narrative_parent: e.target.value } : { narrative_student: e.target.value })}
                                                            disabled={busy || !editable || activeRow.item_status === "SENT"}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                        {/* Right: email / PDF preview */}
                                        <Box sx={{ flex: "1 1 420px", minWidth: 360, position: "sticky", top: 0 }}>
                                            <Tabs value={preview} onChange={(e, v) => setPreview(v)} sx={{ minHeight: 36, mb: 0.5 }}>
                                                <Tab value="email" label="Email" sx={{ minHeight: 36, textTransform: "none" }} icon={<MailOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                                                <Tab value="pdf" label="Attachment" sx={{ minHeight: 36, textTransform: "none" }} icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                                            </Tabs>
                                            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, height: 620, overflow: "hidden", bgcolor: "#fff" }}>
                                                {preview === "email" ? (
                                                    <iframe
                                                        title="Email preview"
                                                        srcDoc={activeAud ? emailPreviewDoc(emailPreviewHtml(activeAud, activeNarrative)) : ""}
                                                        sandbox=""
                                                        style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
                                                    />
                                                ) : pdfUrls[activeItem] ? (
                                                    <iframe title="Attached report" src={pdfUrls[activeItem]} style={{ width: "100%", height: "100%", border: 0 }} />
                                                ) : (
                                                    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                                        {pdfLoading === activeItem
                                                            ? <><CircularProgress size={24} /><Typography variant="caption" color="text.secondary">Rendering the report…</Typography></>
                                                            : <Typography variant="caption" color="text.secondary">Open to render the attachment</Typography>}
                                                    </Box>
                                                )}
                                            </Box>
                                            {preview === "email" && audienceKeys.length > 1 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                                    Showing the {activeAudience === "parent" ? "parent (한국어)" : "student (EN)"} copy — click a card on the left to switch.
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}

                {!loading && step === "sent" && sendResult && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Alert severity={sendResult.failed?.length ? "warning" : "success"}>
                            Sent {sendResult.sent} report{sendResult.sent === 1 ? "" : "s"}{sendResult.failed?.length ? `; ${sendResult.failed.length} failed` : ""}.
                        </Alert>
                        {(sendResult.failed || []).map((f, i) => (
                            <Typography key={i} variant="body2" color="error">
                                <WarningAmberIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                                Student {f.student_id}: {f.reason}
                            </Typography>
                        ))}
                        {!sendResult.failed?.length && (
                            <Typography variant="body2">
                                <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                                All included drafts were emailed to the schedule's recipients.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Close</Button>
                {step === "review" && editable && items.length > 0 && (
                    <>
                        <Button color="inherit" onClick={confirmDiscard} disabled={busy}>Discard batch</Button>
                        <Button onClick={saveAll} disabled={busy || !dirtyIds.length}>
                            {dirtyIds.length ? `Save ${dirtyIds.length}` : "Saved"}
                        </Button>
                        <Button
                            variant="contained" color="primary"
                            onClick={confirmSend}
                            disabled={busy || pendingCount === 0}
                            startIcon={busy ? <CircularProgress size={20} /> : <SendIcon />}
                        >
                            {busy ? "Sending…" : `Send (${pendingCount})`}
                        </Button>
                    </>
                )}
            </DialogActions>

            <ConfirmDialog ref={confirmRef} />
        </Dialog>
    );
};

export default ReviewBatchModal;
